import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import PhotoPlaceholder from "../components/PhotoPlaceholder";
import SEO from "../components/SEO";
import ChapterHero from "../components/ChapterHero";
import NextPage from "../components/NextPage";
import { useCharacterBio } from "../hooks/useCharacterBios";
import useCharacterPhoto from "../hooks/useCharacterPhoto";

/**
 * Shared wide-format group card used for a couple or a sibling group.
 * Loads the photo (if any) from Emergent object storage; otherwise falls
 * back to a landscape placeholder.
 */
function GroupCard({
  slug,
  eyebrow,
  title,
  subtitle,
  body,
  accents,       // 1–3 brand colors used in the top rule + photo frame
  testId,
}) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const bio = useCharacterBio(slug, { role: eyebrow, intro: body });
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const rel = useCharacterPhoto(slug);
  const url = rel ? (rel.startsWith("http") ? rel : `${backendUrl}${rel}`) : null;
  const showPhoto = !!url && !photoFailed;
  const [primary, secondary, tertiary] = accents;
  const frameGradient = tertiary
    ? `linear-gradient(90deg, ${primary} 0%, ${secondary} 50%, ${tertiary} 100%)`
    : `linear-gradient(90deg, ${primary} 0%, ${secondary || primary} 100%)`;

  return (
    <article
      data-testid={testId}
      className="relative bg-[#F9F6F0] border border-[#E7DFD5] rounded-md page-shadow overflow-hidden transition-all duration-700 hover:-translate-y-[2px]"
    >
      {/* multi-tone top rule so the card reads as a joined group */}
      <div className="absolute top-0 left-0 right-0 h-1.5 flex">
        {accents.map((c, i) => (
          <div
            key={i}
            className="h-full"
            style={{ background: c, width: `${100 / accents.length}%` }}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
        {/* Photo — landscape */}
        <div className="md:col-span-3 p-5 md:pr-3">
          {showPhoto ? (
            <div
              data-testid={`photo-${slug}`}
              className="relative w-full overflow-hidden"
              style={{ aspectRatio: "4 / 3" }}
            >
              <div
                className="absolute inset-0 rounded-sm shadow-md"
                style={{ background: frameGradient }}
              />
              <img
                src={url}
                alt={title}
                loading="lazy"
                onError={() => setPhotoFailed(true)}
                className="absolute inset-[10px] w-[calc(100%-20px)] h-[calc(100%-20px)] object-cover rounded-[2px] border border-[#8D6E63]/30"
                draggable="false"
              />
            </div>
          ) : (
            <PhotoPlaceholder
              label={`${title} Photo`}
              aspect="wide"
              accent={primary}
            />
          )}
        </div>

        {/* Copy */}
        <div className="md:col-span-2 p-7 md:pl-5 md:py-8 flex flex-col justify-center text-center md:text-left">
          <p
            className="text-xs uppercase tracking-[0.25em] mb-2"
            style={{ color: secondary || primary }}
          >
            {bio.role}
          </p>
          <h3 className="font-display text-3xl md:text-4xl text-[#4B3A2F] mb-2 leading-tight">
            {title}
          </h3>
          {subtitle && (
            <p className="font-script text-xl text-[#4B3A2F] mb-4">{subtitle}</p>
          )}
          <div
            className="gold-rule mx-auto md:mx-0 mb-4"
            style={{ width: 80 }}
          />
          <p className="font-body text-base md:text-[1.02rem] leading-relaxed text-[#2C2724]/85">
            {bio.intro}
          </p>
          <p className="mt-5 font-script text-lg text-[#4B3A2F]">
            more of their story{" "}
            <span className="coming-soon-flash">coming soon</span>
          </p>
        </div>
      </div>
    </article>
  );
}

export default function MeetTheFamily() {
  return (
    <div className="parchment min-h-screen" data-testid="meet-the-family-page">
      <SEO
        title="Meet the Family"
        description="Behind every gentle story is a family that lived it first. Meet the Texas family who make The Salty Moo™ feel like home."
        path="/meet-the-family"
      />

      <ChapterHero
        path="/meet-the-family"
        image="/assets/heroes/meet-the-family.webp"
        subtitle="Behind every gentle story is a family that lived it first."
        position="top-left"
      />

      <div className="max-w-5xl mx-auto px-6 mt-6 sm:mt-10 relative z-10 pb-24">
        <p className="font-body text-2xl md:text-3xl text-[#2C2724]/85 max-w-3xl mx-auto text-center leading-relaxed mb-14">
          These are the people who make The Salty Moo feel like home.
        </p>

        <div className="flex flex-col gap-10">
          <GroupCard
            testId="bio-card-stefanie-mike"
            slug="stefanie-mike"
            eyebrow="The heart of the herd"
            title="Stefanie & Mike"
            subtitle="(G'Ma & G'Pa)"
            body="Stefanie (G'Ma) gently helps explain the meaning behind Saltie Winks™ and encourages the Lil Salties™ to notice God's love in everyday moments — she represents warmth, reassurance, emotional guidance, faith, and gentle wisdom. Mike (G'Pa) is quiet strength, dependability, emotional steadiness, calm masculinity, and peaceful support — his presence reinforces safety throughout the Salty Moo world. Together they are learning alongside the animals, One Saltie Wink™ at a Time."
            accents={["#579DE6", "#D62839"]}
          />
          <GroupCard
            testId="bio-card-alexis-ethan"
            slug="alexis-ethan"
            eyebrow="Empathy and dependable friendship"
            title="Alexis & Ethan"
            subtitle="connection meets loyalty"
            body="Alexis represents empathy, emotional understanding, and connection — she helps bridge relationships between family, children, and life on the farm. Ethan represents loyalty, calmness, and dependable friendship. Together they help make every ordinary day on the farm feel a little safer and a little brighter."
            accents={["#2DE181", "#C8A876"]}
          />
          <GroupCard
            testId="bio-card-tyler-dawson-saylor"
            slug="tyler-dawson-saylor"
            eyebrow="The next generation of Salties"
            title="Tyler, Dawson & Saylor"
            subtitle="responsibility • curiosity • wonder"
            body="Tyler represents responsibility, reliability, and active helping. Dawson brings playful energy, curiosity, and excitement to the farm. Saylor represents imagination, wonder, creativity, and childlike joy — she naturally notices Saltie Winks™ everywhere. Between them there's a helping hand, a giggle, and a sparkle-lit heart for every kind of day on the farm."
            accents={["#4B3A2F", "#D62839", "#F2C64A"]}
          />
        </div>

        <div className="text-center mt-24 max-w-2xl mx-auto">
          <p className="font-display italic text-3xl md:text-4xl text-[#4B3A2F] leading-relaxed">
            Every family has a story.
            <br />
            This is where ours continues...
          </p>
          <div className="gold-rule my-8" />
        </div>
      </div>
      <NextPage to="/saltie-fun-facts" label="Saltie Fun Facts" />
    </div>
  );
}
